import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { ConstraintNodeMetadata } from '../../explorer/models/nodeTypes';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Constraint-specific context menu action types
 */
type ConstraintContextMenuActionType =
  | 'copy-name'
  | 'script-constraint-as'
  | 'script-constraint-create'
  | 'script-constraint-drop';

const ConstraintActions = {
  COPY_NAME: 'copy-name' as ConstraintContextMenuActionType,
  SCRIPT_AS: 'script-constraint-as' as ConstraintContextMenuActionType,
  SCRIPT_CREATE: 'script-constraint-create' as ConstraintContextMenuActionType,
  SCRIPT_DROP: 'script-constraint-drop' as ConstraintContextMenuActionType,
};

/**
 * Generate context menu actions for a constraint node.
 * SQLite doesn't support ALTER TABLE ADD/DROP CONSTRAINT, so we omit Script as.
 */
export function generateConstraintContextMenuActions(engine?: string): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [
    { id: ConstraintActions.COPY_NAME, label: 'Copy constraint name', icon: 'clipboard' },
  ];

  if (engine !== 'sqlite') {
    actions.push({
      id: ConstraintActions.SCRIPT_AS,
      label: 'Script as',
      icon: 'code',
      submenu: [
        { id: ConstraintActions.SCRIPT_CREATE, label: 'CREATE', icon: 'file-plus' },
        { id: ConstraintActions.SCRIPT_DROP, label: 'DROP', icon: 'trash' },
      ],
    });
  }

  return actions;
}

/**
 * Generate CREATE constraint DDL for CHECK or DEFAULT (engine-aware)
 */
export function generateConstraintCreateDdl(
  metadata: ConstraintNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);

  if (metadata.constraintType === 'CHECK') {
    // PostgreSQL pg_get_constraintdef() returns definition starting with "CHECK (...)"
    // Avoid producing "CHECK CHECK (...)" by detecting if definition already starts with CHECK
    const def = metadata.definition;
    const checkPrefix = /^CHECK\s/i.test(def) ? '' : 'CHECK ';
    return `ALTER TABLE ${fullTable}\nADD CONSTRAINT ${quoteId(metadata.constraintName)} ${checkPrefix}${def};\n`;
  }

  // DEFAULT — engine-specific syntax
  if (engine === 'pgsql') {
    // PostgreSQL uses ALTER COLUMN ... SET DEFAULT
    if (metadata.columnName) {
      return `ALTER TABLE ${fullTable}\nALTER COLUMN ${quoteId(metadata.columnName)} SET DEFAULT ${metadata.definition};\n`;
    }
    return `-- Cannot generate CREATE DEFAULT: column name unknown\n`;
  }

  // MSSQL uses ADD CONSTRAINT ... DEFAULT ... FOR column
  const colClause = metadata.columnName ? ` FOR ${quoteId(metadata.columnName)}` : '';
  return `ALTER TABLE ${fullTable}\nADD CONSTRAINT ${quoteId(metadata.constraintName)} DEFAULT ${metadata.definition}${colClause};\n`;
}

/**
 * Generate DROP constraint DDL (engine-aware)
 */
export function generateConstraintDropDdl(
  metadata: ConstraintNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);

  if (engine === 'mysql') {
    if (metadata.constraintType === 'CHECK') {
      return `ALTER TABLE ${fullTable} DROP CHECK ${quoteId(metadata.constraintName)};\n`;
    }
    // MySQL doesn't have named defaults as constraints — generate a column ALTER instead
    if (metadata.columnName) {
      return `ALTER TABLE ${fullTable} ALTER COLUMN ${quoteId(metadata.columnName)} DROP DEFAULT;\n`;
    }
  }

  if (engine === 'pgsql' && metadata.constraintType === 'DEFAULT') {
    // PostgreSQL DEFAULT constraints use ALTER COLUMN ... DROP DEFAULT
    if (metadata.columnName) {
      return `ALTER TABLE ${fullTable}\nALTER COLUMN ${quoteId(metadata.columnName)} DROP DEFAULT;\n`;
    }
    return `-- Cannot generate DROP DEFAULT: column name unknown\n`;
  }

  // MSSQL, PostgreSQL CHECK, SQLite
  return `ALTER TABLE ${fullTable} DROP CONSTRAINT ${quoteId(metadata.constraintName)};\n`;
}

/**
 * Execute a constraint context menu action
 */
export function executeConstraintContextMenuAction(
  actionId: string,
  metadata: ConstraintNodeMetadata,
): void {
  if (actionId === ConstraintActions.COPY_NAME) {
    void writeClipboardText(metadata.constraintName);
    return;
  }

  if (actionId === ConstraintActions.SCRIPT_CREATE || actionId === ConstraintActions.SCRIPT_DROP) {
    void handleConstraintScript(metadata, actionId);
  }
}

/**
 * Generate constraint script and open in a new query tab
 */
async function handleConstraintScript(
  metadata: ConstraintNodeMetadata,
  actionId: string,
): Promise<void> {
  const { getScriptProvider } = await import('../../scripting');
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');

  const { activeConnection, connection, engine } = getConnectionById(metadata.connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const provider = getScriptProvider(engine);
  const quoteId = (name: string) => provider.quoteIdentifier(name);
  const qualifyTable = (schema: string, table: string) => provider.qualifyTableName(schema, table);

  const isCreate = actionId === ConstraintActions.SCRIPT_CREATE;
  const script = isCreate
    ? generateConstraintCreateDdl(metadata, engine, quoteId, qualifyTable)
    : generateConstraintDropDdl(metadata, engine, quoteId, qualifyTable);

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();
  const titleVerb = isCreate ? 'CREATE' : 'DROP';

  addTab({
    title: `${metadata.constraintName}_${titleVerb}-${queryNumber}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId: metadata.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: metadata.database,
    },
  });
}
